import React, { useState, FormEvent, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import "primeflex/primeflex.css";
import { useToast } from "../../utils/toast";
import aiChatService from "../../services/ai-chat/ai-chat.service";

interface Message {
  id: string;
  sender: "user" | "bot";
  text: string;
}

interface GeminiResponse {
  candidates?: {
    content: {
      parts: {
        text: string;
      }[];
    };
  }[];
  modelVersion?: string;
  responseId?: string;
  usageMetadata?: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
}

// Markdown formatlamasını HTML'e çeviren yardımcı fonksiyon
const convertMarkdownToHtml = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // **text** -> <strong>text</strong>
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // *text* -> <em>text</em>
    .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 border-round text-sm">$1</code>') // `text` -> <code>text</code>
    .replace(/\n/g, '<br />'); // Yeni satırları <br /> ile değiştir
};

const SoruCevapSayfası: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(false);
  const toast = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Backend'e mesaj kaydetme fonksiyonu
  const saveMessageToBackend = async (question: string, answer: string, responseTime: number, modelVersion: string) => {
    try {
      await aiChatService.SaveChatMessage({
        question: question,
        answer: answer,
        ai_model: modelVersion,
        response_time: responseTime
      });
    } catch (error) {
      console.error('Backend kaydetme hatası:', error);
    }
  };

  const sendMessage = async () => {
    const cleanedInput = inputMessage.trim();
    if (!cleanedInput) {
      setButtonIsDisabled(false);
      toast.show("Lütfen geçerli bir mesaj girin.", "error");
      return;
    }

    setIsLoading(true); // Start loading
    const userMessage: Message = {
      id: Date.now().toString(),
      sender: "user",
      text: inputMessage
    };
    setInputMessage("");

    setMessages((prevMessages) => [...prevMessages, userMessage]);

    try {
      const startTime = Date.now();
      const { botResponse, modelVersion } = await handleButtonClick(cleanedInput);
      const responseTime = (Date.now() - startTime) / 1000; // saniye cinsinden
      
      const botMessage: Message = {
        id: Date.now().toString(),
        sender: "bot",
        text: botResponse
      };
      setMessages((prevMessages) => [...prevMessages, botMessage]);

      // Backend'e kaydet
      await saveMessageToBackend(cleanedInput, botResponse, responseTime, modelVersion);
      
    } catch (error) {
      console.error("Error during bot response:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  const handleButtonClick = async (message: string): Promise<{ botResponse: string; modelVersion: string }> => {
    const url =
        "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent?key=AIzaSyDkUuRe_Jxit2qQuA6VwjSfbUNXOADFhFI";

    // Sistem prompt'u - yapay zekanın nasıl davranması gerektiğini belirtir
    const requestBody = {
      contents: [
        {
          role: "user",
          parts: [
            {
              text: "Sen, saygılı, profesyonel ve destekleyici bir yapay zeka asistanısın. Her zaman nazik ve resmi bir üslupla cevap ver. Türkçe dilbilgisi kurallarına uygun, resmi dilde cevaplar sun. Eğer bir konuda emin değilsen, bilgi eksikliğini nazikçe ve resmi bir şekilde ifade edin."
            }
          ]
        },
        {
          role: "user",
          parts: [
            {
              text: message
            }
          ]
        }
      ]
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: GeminiResponse = await response.json();
      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content &&
        data.candidates[0].content.parts &&
        data.candidates[0].content.parts.length > 0
      ) {
        const botResponse = data.candidates[0].content.parts[0].text;
        const modelVersion = data.modelVersion || 'gemini-1.5-flash';
        return { botResponse, modelVersion };
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (error) {
      toast.show(`Bir hata oluştu.`, "error");
      return { botResponse: "Bir hata oluştu.", modelVersion: 'gemini-1.5-flash' };
    }
  };

  return (
    <div className="card flex flex-column p-3 sm:p-4" style={{ minHeight: "75vh", height: "calc(100vh - 12rem)" }}>
      {/* Üst Bar */}
      <div className="flex justify-content-between align-items-center pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-comments text-xl text-primary"></i>
          <span className="font-semibold text-lg">Yapay Zeka Asistanı</span>
        </div>
        <Button
          label="Yeni Sohbet"
          icon="pi pi-refresh"
          size="small"
          outlined
          onClick={() => {
            setMessages([]);
            setInputMessage("");
          }}
        />
      </div>

      {/* Mesaj Listesi */}
      <div className="flex-1 overflow-y-auto py-3 px-1 flex flex-column gap-3">
        {messages.length === 0 ? (
          <div className="flex flex-column align-items-center justify-content-center h-full text-500 py-6 my-auto">
            <i className="pi pi-sparkles text-4xl mb-3 text-primary"></i>
            <p className="font-semibold text-xl mb-1 text-color">Yapay Zeka Asistanına Hoş Geldiniz</p>
            <span className="text-sm text-center max-w-20rem">OSB ve sanayi ile ilgili sorularınızı sorabilir, bilgi alabilirsiniz.</span>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === "user" ? "justify-content-end" : "justify-content-start"}`}
            >
              <div
                className={`p-3 border-round-xl shadow-1 ${
                  message.sender === "user"
                    ? "bg-primary text-white"
                    : "surface-card border-1 surface-border text-color"
                }`}
                style={{ maxWidth: "85%", wordBreak: "break-word" }}
              >
                <div className={`font-semibold text-xs mb-1 ${message.sender === "user" ? "text-white-alpha-80" : "text-500"}`}>
                  {message.sender === "user" ? "Siz" : "Yapay Zeka"}
                </div>
                {message.sender === "bot" ? (
                  <div
                    className="line-height-3 text-sm sm:text-base"
                    dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(message.text) }}
                  />
                ) : (
                  <div className="line-height-3 text-sm sm:text-base">{message.text}</div>
                )}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-content-start">
            <div className="surface-card border-1 surface-border border-round-xl p-3 flex align-items-center gap-2">
              <i className="pi pi-spin pi-spinner text-primary"></i>
              <span className="text-sm text-500">Cevap hazırlanıyor...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Giriş Alanı */}
      <form
        className="flex gap-2 align-items-center pt-3 border-top-1 surface-border"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          sendMessage();
        }}
      >
        <div className="flex-grow-1">
          <InputText
            id="searchWord"
            name="searchWord"
            className="w-full"
            placeholder="Mesajınızı buraya yazın..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={buttonIsDisabled || isLoading}
          />
        </div>
        <div style={{ flexShrink: 0 }}>
          <Button
            label={isLoading ? "" : "Gönder"}
            icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-send"}
            type="submit"
            disabled={buttonIsDisabled || isLoading || inputMessage.trim() === ""}
          />
        </div>
      </form>
    </div>
  );
};

export default SoruCevapSayfası;
